import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Grid,
  Card,
  Divider,
  Tabs,
  Tab,
  Badge,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as PackageIcon,
  Straighten as RulerIcon,
  Error as AlertCircleIcon,
  Info as InfoIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import { fetchDataEngine } from "../../../data/Axios/DynamicService";
import { salesManagement } from "../../../config";
import axios from "axios";
import Swal from "sweetalert2";

const RetailDistribution = ({ open, onClose, data, onAction }) => {
  

  const [colorDistributions, setColorDistributions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [distributionData, setDistributionData] = useState([]);
  const [activeColorId, setActiveColorId] = useState(null);

  const productCode = data?.productCode;
  const productName = data?.productName;
  const purchaseOrderId = data?.purchaseOrderId;
  const puchaseOrderItemsId = data?.id;

  const swalTop = Swal.mixin({
    didOpen: () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "99999"; // or higher than your MUI modal
      }
    },
  });
  const FetchData = async () => {
    const result = await fetchDataEngine(
      "warehouseDistributionByColumnValues",
      "purchaseOrderItemView",
      {
        productCode: data.productCode,
        productId: data.productId,
      },
      [
        "purchaseOrderId",
        "productCode",
        "branchId",
        "colorId",
        "colorName",
        "sizeRange",
        "minSize",
        "maxSize",
        "quantity",
        "averageCost",
        "createdDate",
        "createdBy",
        "companyId",
        "regionId",
        "quantityPerCarton",
        "productId",
        "purchaseOrderItemsId",
      ],
      salesManagement.uri
    );
    if (result) {
      setDistributionData(result);
    }
  };

  useEffect(() => {
    if (data?.productCode && data?.productId) {
      FetchData();
    }
  }, [data]);

  useEffect(() => {
    if (open && distributionData.length > 0) {
      const initialDistributions = {};

      distributionData.forEach((color) => {
        // Calculate quantity based on quantityPerCarton * data.carton
        const calculatedQuantity =
          (color.quantityPerCarton || 0) * (data.cartons || 0);

        initialDistributions[color.colorId] = {
          ...color,
          quantity: calculatedQuantity, // Override with calculated quantity
          rows: [{ id: 1, size: "", quantity: "" }],
          totalEntered: 0,
        };
      });

      setColorDistributions(initialDistributions);
      setActiveColorId(distributionData[0].colorId);
    }
  }, [open, distributionData, data.carton]);

  const handleTabChange = (event, newValue) => {
    setActiveColorId(newValue);
  };

  const addRow = (colorId) => {
    setColorDistributions((prev) => {
      const colorDist = prev[colorId];
      const newId =
        colorDist.rows.length > 0
          ? Math.max(...colorDist.rows.map((r) => r.id)) + 1
          : 1;
      return {
        ...prev,
        [colorId]: {
          ...colorDist,
          rows: [...colorDist.rows, { id: newId, size: "", quantity: "" }],
        },
      };
    });
  };

  const removeRow = (colorId, rowId) => {
    setColorDistributions((prev) => {
      const colorDist = prev[colorId];
      const updated = colorDist.rows.filter((row) => row.id !== rowId);
      return {
        ...prev,
        [colorId]: {
          ...colorDist,
          rows:
            updated.length > 0 ? updated : [{ id: 1, size: "", quantity: "" }],
        },
      };
    });
  };

  const updateRow = (colorId, rowId, field, value) => {
    setColorDistributions((prev) => {
      const colorDist = prev[colorId];
      const updatedRows = colorDist.rows.map((row) => {
        if (row.id === rowId) {
          if (field === "quantity") {
            const newQty = Number(value) || 0;
            const otherTotal = colorDist.rows.reduce(
              (acc, r) =>
                r.id === rowId ? acc : acc + (Number(r.quantity) || 0),
              0
            );
            if (otherTotal + newQty > colorDist.quantity) return row;
          }
          // Allow size to be set even if out of range (will show error in UI)
          return { ...row, [field]: value };
        }
        return row;
      });

      const totalEntered = updatedRows.reduce(
        (acc, row) => acc + (Number(row.quantity) || 0),
        0
      );

      return {
        ...prev,
        [colorId]: {
          ...colorDist,
          rows: updatedRows,
          totalEntered,
        },
      };
    });
  };

  const isSizeUsed = (colorId, size, currentRowId) => {
    const colorDist = colorDistributions[colorId];
    return colorDist.rows.some(
      (row) => row.id !== currentRowId && row.size === size && size !== ""
    );
  };

  const getColorStatus = (colorDist) => {
    const isComplete =
      colorDist.totalEntered === colorDist.quantity &&
      colorDist.totalEntered > 0;
    const isOverCount = colorDist.totalEntered > colorDist.quantity;

    if (isComplete) return { status: "complete", color: "success" };
    if (isOverCount) return { status: "error", color: "error" };
    return { status: "pending", color: "default" };
  };

  const validateColor = (colorDist) => {
    const { rows, quantity, totalEntered, minSize, maxSize } = colorDist;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.size || !row.quantity || Number(row.quantity) <= 0) {
        return { valid: false, message: `Please complete row ${i + 1}` };
      }

      const sizeNum = Number(row.size);
      if (sizeNum < minSize || sizeNum > maxSize) {
        return {
          valid: false,
          message: `Row ${i + 1}: Size ${
            row.size
          } must be between ${minSize} and ${maxSize}`,
        };
      }

      if (isSizeUsed(colorDist.colorId, row.size, row.id)) {
        return {
          valid: false,
          message: `Size ${row.size} is already entered`,
        };
      }
    }

    if (totalEntered !== quantity) {
      return {
        valid: false,
        message: `Total (${totalEntered}) must match expected (${quantity})`,
      };
    }

    return { valid: true };
  };

  const handleSubmit = async () => {
    const rawToken = localStorage.getItem("token");
    const cleanToken = rawToken?.replace(/^"|"$/g, "") || "";
    // Validate all colors first
    for (const colorId in colorDistributions) {
      const colorDist = colorDistributions[colorId];
      const validation = validateColor(colorDist);

      if (!validation.valid) {
        alert(`${colorDist.colorName}: ${validation.message}`);
        setActiveColorId(colorDist.colorId);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Transform data to match GraphQL mutation structure
      const input = Object.values(colorDistributions)
        .map((colorDist) => ({
          purchaseOrderId: data.purchaseOrderId,
          purchaseOrderItemsId: data.id,
          companyId: colorDist.companyId,
          colorId: colorDist.colorId,
          distributions: colorDist.rows
            .filter((row) => row.size && row.quantity)
            .map((row) => ({
              size: String(row.size),
              quantity: Number(row.quantity),
            })),
        }))
        .filter((item) => item.distributions.length > 0);

      if (input.length === 0) {
        throw new Error("No valid distributions to save");
      }

      // Convert to GraphQL literal string
      const inputAsGraphQLLiteral = JSON.stringify(input).replace(
        /"([^"]+)":/g,
        "$1:"
      ); // remove quotes around keys for GraphQL syntax

      // Build the mutation string
      const mutation = `
  mutation {
    retailDistribution(distributions: ${inputAsGraphQLLiteral}) {
      purchaseOrderId
      purchaseOrderItemsId
      companyId
      colorId
      size
      quantity
      createdBy
    }
  }
`;
      // Make GraphQL API call
      const response = await axios.post(
        salesManagement.uri,
        { query: mutation },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cleanToken}`,
          },
        }
      );

      const totalPairs = Object.values(colorDistributions).reduce(
        (sum, cd) => sum + cd.totalEntered,
        0
      );

      // Check for GraphQL errors
      if (response.status == 200) {
        alert(
          `✓ Distribution Saved Successfully!\n\n${totalPairs} pairs distributed across ${
            Object.keys(colorDistributions).length
          } colors. To view Distribution details, please check the Retail Inventory Management > Retail Distributed Logs`
        );
        onAction?.(); // Callback to refresh parent data
        onClose?.(); // Close the dialog
      }
    } catch (error) {
      swalTop.fire({
        icon: "error",
        title: "Error!",
        text:
          error.response.data.errors[0].message ||
          "An error occurred while saving warehouse distribution.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAllValid = Object.values(colorDistributions).every((colorDist) => {
    const validation = validateColor(colorDist);
    return validation.valid;
  });

  const totalExpected = Object.values(colorDistributions).reduce(
    (sum, cd) => sum + cd.quantity,
    0
  );
  const totalEntered = Object.values(colorDistributions).reduce(
    (sum, cd) => sum + cd.totalEntered,
    0
  );

  const activeColor = colorDistributions[activeColorId];

  if (!activeColor) return null;

  const progressPercentage =
    activeColor.quantity > 0
      ? (activeColor.totalEntered / activeColor.quantity) * 100
      : 0;
  const isComplete =
    activeColor.totalEntered === activeColor.quantity &&
    activeColor.totalEntered > 0;
  const isOverCount = activeColor.totalEntered > activeColor.quantity;
  const remainingQuantity = Math.max(
    activeColor.quantity - activeColor.totalEntered,
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PackageIcon sx={{ color: "primary.main", fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Retail Distribution Entry
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {productCode} - {productName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Overall Progress
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {totalEntered} / {totalExpected}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ "&:hover": { bgcolor: "grey.100" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Color Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <Tabs
          value={activeColorId}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 64,
            "& .MuiTab-root": {
              minHeight: 64,
              fontSize: "0.95rem",
              fontWeight: 600,
            },
          }}
        >
          {Object.values(colorDistributions).map((colorDist) => {
            const status = getColorStatus(colorDist);
            return (
              <Tab
                key={colorDist.colorId}
                value={colorDist.colorId}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircleIcon
                        sx={{
                          fontSize: 10,
                          color:
                            status.status === "complete"
                              ? "success.main"
                              : status.status === "error"
                              ? "error.main"
                              : "grey.400",
                        }}
                      />
                      {colorDist.colorName}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {colorDist.totalEntered}/{colorDist.quantity}
                    </Typography>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "auto",
        }}
      >
        {/* Color Info Card */}
        <Paper
          elevation={0}
          sx={{
            background: isComplete
              ? "linear-gradient(135deg, #059669 0%, #10b981 100%)"
              : isOverCount
              ? "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
              : "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
            borderRadius: 3,
            p: 3,
            color: "white",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Color
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {activeColor.colorName}
              </Typography>
            </Grid>
            <Grid item xs={4} md={3}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Expected
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {activeColor.quantity}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                pairs (
                {Math.ceil(
                  activeColor.quantity / activeColor.quantityPerCarton
                )}{" "}
                cartons)
              </Typography>
            </Grid>
            <Grid item xs={4} md={3}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Size Range
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {activeColor.minSize}-{activeColor.maxSize}
              </Typography>
            </Grid>
            <Grid item xs={4} md={3}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Progress
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {activeColor.totalEntered}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                entered
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(progressPercentage, 100)}
              sx={{
                height: 8,
                borderRadius: 10,
                bgcolor: "rgba(255, 255, 255, 0.2)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        </Paper>

        {/* Status Alert */}
        {!isComplete && !isOverCount && (
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>{remainingQuantity}</strong> pairs remaining to distribute
              for {activeColor.colorName}
            </Typography>
          </Alert>
        )}

        {isComplete && (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="body2" fontWeight="500">
              Perfect! All {activeColor.quantity} pairs counted for{" "}
              {activeColor.colorName}
            </Typography>
          </Alert>
        )}

        {isOverCount && (
          <Alert severity="error" icon={<AlertCircleIcon />}>
            <Typography variant="body2">
              <strong>
                Over by {activeColor.totalEntered - activeColor.quantity}
              </strong>{" "}
              pairs. Please check your counts.
            </Typography>
          </Alert>
        )}

        {/* Size Distribution Rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {activeColor.rows.map((row, index) => (
            <Paper
              key={row.id}
              elevation={0}
              sx={{
                p: 2,
                border: "2px solid",
                borderColor: "grey.300",
                borderRadius: 2,
                bgcolor: "grey.50",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.light",
                  boxShadow: 1,
                },
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={1}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    {index + 1}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    color="text.secondary"
                    gutterBottom
                    sx={{ display: "block" }}
                  >
                    Size <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={row.size}
                    onChange={(e) =>
                      updateRow(
                        activeColor.colorId,
                        row.id,
                        "size",
                        e.target.value
                      )
                    }
                    placeholder={`${activeColor.minSize}-${activeColor.maxSize}`}
                    inputProps={{
                      min: activeColor.minSize,
                      max: activeColor.maxSize,
                      step: 0.5,
                    }}
                    fullWidth
                    size="small"
                    error={
                      row.size &&
                      (isSizeUsed(activeColor.colorId, row.size, row.id) ||
                        Number(row.size) < activeColor.minSize ||
                        Number(row.size) > activeColor.maxSize)
                    }
                    helperText={
                      row.size
                        ? isSizeUsed(activeColor.colorId, row.size, row.id)
                          ? "Already entered"
                          : Number(row.size) < activeColor.minSize ||
                            Number(row.size) > activeColor.maxSize
                          ? `Size must be between ${activeColor.minSize} and ${activeColor.maxSize}`
                          : ""
                        : ""
                    }
                    InputProps={{
                      startAdornment: (
                        <RulerIcon
                          sx={{ mr: 1, color: "text.disabled", fontSize: 20 }}
                        />
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    color="text.secondary"
                    gutterBottom
                    sx={{ display: "block" }}
                  >
                    Quantity <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={row.quantity}
                    onChange={(e) =>
                      updateRow(
                        activeColor.colorId,
                        row.id,
                        "quantity",
                        e.target.value
                      )
                    }
                    placeholder="Count"
                    inputProps={{ min: 1 }}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={10} sm={2}>
                  <Card
                    variant="outlined"
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      border: "2px solid",
                      borderColor:
                        row.size && row.quantity ? "success.light" : "grey.300",
                      bgcolor:
                        row.size && row.quantity ? "success.lighter" : "white",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={
                        row.size && row.quantity
                          ? "success.dark"
                          : "text.disabled"
                      }
                    >
                      {row.quantity || "—"}
                    </Typography>
                  </Card>
                </Grid>

                <Grid
                  item
                  xs={2}
                  sm={1}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <IconButton
                    onClick={() => removeRow(activeColor.colorId, row.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            mt: 1,
          }}
        >
          <Button
            onClick={() => addRow(activeColor.colorId)}
            variant="outlined"
            startIcon={<AddIcon />}
            size="large"
            disabled={activeColor.totalEntered >= activeColor.quantity}
            sx={{
              borderWidth: 2,
              px: 4,
              "&:hover": { borderWidth: 2 },
            }}
          >
            Add Size Row
          </Button>
          {activeColor.totalEntered >= activeColor.quantity && (
            <Typography variant="caption" color="text.secondary">
              All {activeColor.quantity} pairs have been allocated
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
          <Typography variant="body2" color="text.secondary">
            Completed:
          </Typography>
          {Object.values(colorDistributions).map((cd) => {
            const status = getColorStatus(cd);
            return (
              <Chip
                key={cd.colorId}
                label={cd.colorName}
                size="small"
                color={status.color}
                variant={status.status === "complete" ? "filled" : "outlined"}
              />
            );
          })}
        </Box>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ px: 3 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isAllValid || isSubmitting}
          variant="contained"
          startIcon={isAllValid && <CheckCircleIcon />}
          sx={{ px: 4, boxShadow: 3 }}
        >
          {isSubmitting ? "Saving..." : "Save All Distributions"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetailDistribution;
